/**
 * RoutePrioritySelector - Asks user to choose route priority
 * Shows BEFORE route calculation: Safest Air or Fastest Route
 */

import { motion } from 'framer-motion';
import { useNavigationStore, type RoutePriority } from '@/stores/useNavigationStore';
import { GlassPanel } from '@/components/common';
import { cn } from '@/utils/cn';

interface RoutePrioritySelectorProps {
  className?: string;
  onSelect: (priority: RoutePriority) => void;
}

export function RoutePrioritySelector({ className, onSelect }: RoutePrioritySelectorProps) {
  const selectedDestination = useNavigationStore((state) => state.selectedDestination);
  const clearSearch = useNavigationStore((state) => state.clearSearch);

  if (!selectedDestination) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={cn('w-full max-w-md', className)}
    >
      <GlassPanel className="space-y-5">
        {/* Header */}
        <div className="text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-hud-primary/20 flex items-center justify-center mb-3">
            <RouteIcon className="w-6 h-6 text-hud-primary" />
          </div>
          <h2 className="font-hud text-lg text-hud-primary uppercase tracking-wider">
            Choose Your Priority
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            How should we plan your route to <span className="text-hud-primary">{selectedDestination.name}</span>?
          </p>
        </div>

        {/* Priority Options */}
        <div className="space-y-3">
          {/* Safest Air Option */}
          <button
            onClick={() => onSelect('safest')}
            className={cn(
              'w-full p-4 rounded-xl text-left transition-all',
              'bg-hud-success/10 border-2 border-hud-success/40',
              'hover:bg-hud-success/20 hover:border-hud-success/60',
              'group'
            )}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-hud-success/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <LeafIcon className="w-6 h-6 text-hud-success" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-hud text-base text-hud-success uppercase">
                    Safest Air Route
                  </h3>
                  <span className="text-[10px] bg-hud-success text-bg-deep px-2 py-0.5 rounded-full font-bold">
                    RECOMMENDED
                  </span>
                </div>
                <p className="text-xs text-text-secondary mt-1">
                  Prioritizes parks, backstreets, and low-pollution areas. Better for your brain health.
                </p>
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="text-hud-success">+ Higher Neural Index</span>
                  <span className="text-text-muted">May take longer</span>
                </div>
              </div>
            </div>
          </button>

          {/* Fastest Route Option */}
          <button
            onClick={() => onSelect('fastest')}
            className={cn(
              'w-full p-4 rounded-xl text-left transition-all',
              'bg-hud-warning/10 border-2 border-hud-warning/40',
              'hover:bg-hud-warning/20 hover:border-hud-warning/60',
              'group'
            )}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-hud-warning/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <SpeedIcon className="w-6 h-6 text-hud-warning" />
              </div>
              <div className="flex-1">
                <h3 className="font-hud text-base text-hud-warning uppercase">
                  Fastest Route
                </h3>
                <p className="text-xs text-text-secondary mt-1">
                  Takes the quickest path using main roads. Gets you there faster.
                </p>
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="text-hud-warning">+ Shorter time</span>
                  <span className="text-text-muted">Higher pollution exposure</span>
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Cancel Button */}
        <button
          onClick={clearSearch}
          className={cn(
            'w-full py-2 px-4 rounded-lg',
            'bg-glass-bg border border-glass-border',
            'text-text-secondary text-sm font-hud uppercase tracking-wider',
            'hover:bg-hud-primary/10 hover:text-hud-primary transition-colors'
          )}
        >
          Cancel
        </button>
      </GlassPanel>
    </motion.div>
  );
}

// Icons
function RouteIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  );
}

function LeafIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </svg>
  );
}

function SpeedIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

export default RoutePrioritySelector;
